import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle2,
  Eye,
  Plus,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useAuthUser } from "../../../hooks/useAuthUser";
import SelectReact from "react-select";
import RetreatApi from "../../../api/retreatApi";
import AdminApi from "../../../api/adminApi";
import AuthApi from "../../../api/authApi";
import PriestApi from "../../../api/priestApi";
import SampleContentApi from "../../../api/sampleContentApi";
import ViewMailMsg from "../../../components/ViewMailMsg";
import AlertDelete from "../../../components/AlertDelete";
import type { LifecycleRetreat } from "../../../types/retreatLifecycle";
import { formatCoupleOptionLabel } from "../../../utils/coupleLabels";
import { formatParishOptionLabel } from "../../../utils/parishLabels";
import { formatLanguage } from "./lifecycle/lifecycleUi";
import SetupRichEditor from "./lifecycle/SetupRichEditor";
import { htmlHasText } from "../../../utils/htmlText";
import {
  processRetreatImage,
  RETREAT_IMAGE_ACCEPT,
  getRetreatImagePreviewSrc,
} from "../../../utils/processRetreatImage";
import type { SeasonOption } from "../../../types/retreatLifecycle";
import WeekendDayFields, {
  formatScheduleDate,
  toTimePickerValue,
  weekendScheduleFromDateFrom,
} from "./lifecycle/WeekendDayFields";
import uploadRetreat from "../../../assets/images/uploadRetreat.png";
import corruptedImg from "../../../assets/images/corrupted-img.png";
import {
  DASHBOARD_FONT,
  DASHBOARD_MUTED,
  DASHBOARD_NAVY,
  DASHBOARD_NAVY_HOVER,
  DASHBOARD_TEXT,
} from "./manage/dashboardUi";
import TeamFormationTab from "./setup/TeamFormationTab";
import PublicationReadinessDashboard from "./setup/PublicationReadinessDashboard";
import { computeSetupReadinessAreas } from "./setup/setupReadinessAreas";
import type { TeamFormationSummary } from "./setup/teamFormationTypes";
import { computeSummaryFromCandidates, MOCK_CANDIDATES } from "./setup/teamFormationMockData";

const SETUP_THEME = createTheme({
  palette: {
    primary: {
      main: DASHBOARD_NAVY,
      dark: DASHBOARD_NAVY_HOVER,
      contrastText: "#ffffff",
    },
  },
  typography: {
    fontFamily: DASHBOARD_FONT,
  },
});

const RETREAT_TYPE_OPTIONS = [
  "Established Parish",
  "Outreach / New Parish",
  "Bishop-Sponsored",
];

const SEASON_OPTIONS: SeasonOption[] = ["Winter", "Spring", "Summer", "Fall"];

const LEADERSHIP_ROLES = [
  { key: "lead_couple", label: "Lead Couple", kind: "couple" },
  { key: "co_lead_couple", label: "Co-Lead Couple", kind: "couple" },
  { key: "mentor_couple", label: "Mentor Couple", kind: "couple" },
  {
    key: "spiritual_mentor_couple",
    label: "Spiritual Mentor Couple",
    kind: "couple",
  },
  {
    key: "spiritual_director",
    label: "Spiritual Director (Priest/Deacon)",
    kind: "priest",
  },
] as const;

type LeadershipRoleKey = (typeof LEADERSHIP_ROLES)[number]["key"];

function coupleCardName(c: any) {
  if (!c) return "";
  const last = c.hisLastName || c.herLastName || "";
  const his = c.hisFirstName || "";
  const her = c.herFirstName || "";
  if (his && her) return `${his} & ${her} ${last}`.trim();
  return formatCoupleOptionLabel(c);
}

function priestCardName(p: any) {
  if (!p) return "";
  return `${p.title ? `${p.title} ` : ""}${p.firstName || ""} ${
    p.lastName || p.name || ""
  }`.trim();
}

function initialsFromName(name: string) {
  const parts = String(name || "")
    .replace(/&/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

type DaySchedule = {
  day: string;
  date: string;
  from: string;
  to: string;
};

type LeadershipState = {
  leadCoupleId: number | "";
  coLeadCoupleId: number | "";
  mentorCoupleId: number | "";
  spiritualMentorCoupleId: number | "";
  spiritualDirectorPriestId: number | "";
};

const setupFieldId = (id: string) => `setup-field-${id}`;

const leadershipSetupFieldId = (key: string) => {
  if (key === "lead_couple") return setupFieldId("lead");
  if (key === "co_lead_couple") return setupFieldId("coLead");
  if (key === "mentor_couple") return setupFieldId("mentor");
  return setupFieldId(key);
};

const fieldAnchorSx = (highlighted?: boolean) => ({
  scrollMarginTop: 96,
  borderRadius: 1.5,
  transition: "box-shadow 0.35s ease",
  boxShadow: highlighted ? "0 0 0 3px rgba(37, 99, 235, 0.55)" : "none",
});

function scrollToSetupField(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const focusable = el.querySelector<HTMLElement>(
    "input:not([type='file']):not([hidden]), textarea, select, button, [contenteditable='true']",
  );
  window.setTimeout(() => focusable?.focus({ preventScroll: true }), 450);
}

function SectionCard({
  id,
  title,
  subtitle,
  actions,
  children,
  highlighted,
}: {
  id?: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <Paper
      id={id}
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        height: "100%",
        fontFamily: DASHBOARD_FONT,
        ...fieldAnchorSx(highlighted),
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: DASHBOARD_FONT,
              fontWeight: 800,
              fontSize: "1.0625rem",
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontFamily: DASHBOARD_FONT,
                mt: 0.35,
                fontSize: "0.8125rem",
                color: "text.secondary",
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
}

function SampleLink({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Typography
      component="span"
      onClick={onClick}
      sx={{
        fontFamily: DASHBOARD_FONT,
        color: DASHBOARD_NAVY,
        textDecoration: "underline",
        cursor: "pointer",
        fontWeight: 700,
        fontSize: "0.8125rem",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
  );
}

function fallbackDescriptionHtml(lang: string) {
  if (lang === "spanish") {
    return `<div style="text-align:center">
      <p style="color:rgb(53,53,53);font-size:16px">El costo es de $175 por pareja. Se requiere un depósito de $75 al momento de enviar el formulario de inscripción. El número de parejas que podemos acomodar es limitado y la disponibilidad es por orden de llegada. El costo de inscripción cubre la cena del viernes, tres comidas el sábado y el desayuno y almuerzo del domingo. También cubre todos los materiales del retiro. No se proporciona cuidado de niños.</p>
      <p><strong><em>Todas las parejas del retiro deben comprometerse a asistir a todas las actividades del fin de semana de 3 días.</em></strong></p>
      <p>¡Gracias!</p>
    </div>`;
  }
  return `<div style="text-align:center">
    <p style="color:rgb(53,53,53);font-size:16px">The cost is $175 per couple. A $75 deposit is due with the registration form. We are limited as to the number of couples we can accommodate, and availability is on a first come, first-serve basis. The registration cost covers dinner on Friday, three meals on Saturday, and breakfast and lunch on Sunday. It will also cover all retreat materials. Childcare is not provided.</p>
    <p><strong><em>All Retreat Couples must commit to attending the entire 3-day weekend’s activities.</em></strong></p>
    <p>Thank you!</p>
  </div>`;
}

function fallbackEmailHtml(lang: string) {
  if (lang === "spanish") {
    return `<div style="text-align:center">
      <p>Gracias por inscribirse en el retiro matrimonial LOVESTRONG. ¡Nos entusiasma que nos acompañen!</p>
      <p>Si tienes alguna pregunta, contáctanos aquí...</p>
      <p style="font-size:12px"><strong>¡VIVE! ¡AMA! ¡FUERTE!</strong></p>
    </div>`;
  }
  return `<div style="text-align:center">
    <p>Thank you for registering for the LOVESTRONG Marriage retreat. We are excited that you will join us!</p>
    <p>If you have any questions, please reach out to us here...</p>
    <p style="font-size:12px">Please expect to hear from us soon!</p>
  </div>`;
}

const RetreatSetupWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = useAuthUser();
  const retreatApi = new RetreatApi();
  const adminApi = new AdminApi();
  const authApi = new AuthApi();
  const priestApi = new PriestApi();
  const sampleContentApi = new SampleContentApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retreat, setRetreat] = useState<LifecycleRetreat | null>(null);
  const [couples, setCouples] = useState<any[]>([]);
  const [parishes, setParishes] = useState<any[]>([]);
  const [priests, setPriests] = useState<any[]>([]);
  const [parishId, setParishId] = useState<number | "">("");
  const [language, setLanguage] = useState("english");
  const [assignRole, setAssignRole] = useState<LeadershipRoleKey | null>(null);
  const [assignValue, setAssignValue] = useState<any>(null);
  const [approving, setApproving] = useState(false);
  const [editorsReady, setEditorsReady] = useState(false);

  const [themeTitle, setThemeTitle] = useState("");
  const [themeImage, setThemeImage] = useState("");
  const [missionStatement, setMissionStatement] = useState("");
  const [retreatDetails, setRetreatDetails] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [location, setLocation] = useState("");
  const [friday, setFriday] = useState<DaySchedule>({
    day: "Friday",
    date: "",
    from: "",
    to: "",
  });
  const [saturday, setSaturday] = useState<DaySchedule>({
    day: "Saturday",
    date: "",
    from: "",
    to: "",
  });
  const [sunday, setSunday] = useState<DaySchedule>({
    day: "Sunday",
    date: "",
    from: "",
    to: "",
  });
  const [sameTimeError, setSameTimeError] = useState("");
  const [leadership, setLeadership] = useState<LeadershipState>({
    leadCoupleId: "",
    coLeadCoupleId: "",
    mentorCoupleId: "",
    spiritualMentorCoupleId: "",
    spiritualDirectorPriestId: "",
  });
  const [retreatType, setRetreatType] = useState<string>("");
  const [season, setSeason] = useState<SeasonOption | "">("");
  const [seasonYear, setSeasonYear] = useState<number | "">("");
  const [planningNotes, setPlanningNotes] = useState("");
  const [subAdmins, setSubAdmins] = useState<Array<{ label: string; value: number }>>([]);
  const [adminSelect, setAdminSelect] = useState<any>(null);
  const [adminTick, setAdminTick] = useState(false);
  const [committeeMembers, setCommitteeMembers] = useState<
    Array<{ label: string; value: number }>
  >([]);
  const [committeeSelect, setCommitteeSelect] = useState<any>([]);
  const [committeeTick, setCommitteeTick] = useState(false);
  const [themeImageFile, setThemeImageFile] = useState<File | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [descriptionSample, setDescriptionSample] = useState(false);
  const [mailSample, setMailSample] = useState(false);
  const [mailMsgView, setMailMsgView] = useState(false);
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const [dynamicDescription, setDynamicDescription] = useState("");
  const [dynamicEmailMsg, setDynamicEmailMsg] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const initialTeamSummary = useMemo(
    () => computeSummaryFromCandidates(MOCK_CANDIDATES),
    [],
  );
  const [teamFormationSummary, setTeamFormationSummary] =
    useState<TeamFormationSummary>(initialTeamSummary);
  const [teamFormationReviewStatus, setTeamFormationReviewStatus] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get("tab") === "team-formation" ? 1 : 0,
  );

  const handleTeamSummaryChange = useCallback((summary: TeamFormationSummary) => {
    setTeamFormationSummary(summary);
  }, []);

  const handleTeamReviewStatusChange = useCallback((status: string | null) => {
    setTeamFormationReviewStatus(status);
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setActiveTab(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 1) next.set("tab", "team-formation");
        else next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  const goToField = (targetIdOrItemId: string) => {
    const targetId = targetIdOrItemId.startsWith("setup-field-")
      ? targetIdOrItemId
      : setupFieldId(targetIdOrItemId);
    scrollToSetupField(targetId);
    setHighlightedFieldId(targetId);
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedFieldId((cur) => (cur === targetId ? null : cur));
    }, 1800);
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const actorPayload = () => ({
    actorName: userData?.userName || "Admin",
    actorRole: userData?.userRole || "ADMIN",
    actorUserId: userData?.id || null,
  });

  const coupleOptions = useMemo(
    () =>
      (couples || []).map((c) => ({
        value: c.id as number,
        label: formatCoupleOptionLabel(c),
      })),
    [couples],
  );

  const assignedLeadershipIds = useMemo(
    () =>
      new Set(
        [
          leadership.leadCoupleId,
          leadership.coLeadCoupleId,
          leadership.mentorCoupleId,
          leadership.spiritualMentorCoupleId,
        ]
          .filter((id) => id !== "" && id != null)
          .map((id) => Number(id)),
      ),
    [leadership],
  );

  const leadershipCoupleOptions = useMemo(
    () =>
      coupleOptions.filter(
        (opt) => !assignedLeadershipIds.has(Number(opt.value)),
      ),
    [coupleOptions, assignedLeadershipIds],
  );

  const rtpCoupleOptions = useMemo(
    () =>
      coupleOptions.filter(
        (opt) => !subAdmins.some((item) => Number(item.value) === Number(opt.value)),
      ),
    [coupleOptions, subAdmins],
  );

  const committeeCoupleOptions = useMemo(
    () =>
      coupleOptions.filter(
        (opt) =>
          !committeeMembers.some((item) => Number(item.value) === Number(opt.value)),
      ),
    [coupleOptions, committeeMembers],
  );

  const priestOptions = useMemo(
    () =>
      (priests || []).map((p) => ({
        value: p.id as number,
        label: priestCardName(p) + (p.email ? ` - ${p.email}` : ""),
      })),
    [priests],
  );

  const matchCoupleId = (
    team: any[],
    role: string,
    coupleList: any[],
    fallbackId?: number | "",
  ) => {
    if (fallbackId) return fallbackId;
    const member = Array.isArray(team)
      ? team.find((m) => m.role === role)
      : null;
    if (!member) return "";
    const emails = [member.hisEmail, member.herEmail]
      .filter(Boolean)
      .map((e: string) => String(e).toLowerCase());
    const found = (coupleList || []).find(
      (c) =>
        emails.includes(String(c.hisEmail || "").toLowerCase()) ||
        emails.includes(String(c.herEmail || "").toLowerCase()),
    );
    return found?.id || "";
  };

  const hydrate = (data: LifecycleRetreat, coupleList: any[] = couples) => {
    setRetreat(data);
    setParishId(data.parishId || "");
    setLanguage(String(data.language || "english").toLowerCase());
    setThemeTitle(data.heading || "");
    setThemeImage(data.image1 || "");
    setMissionStatement(data.mission_statement || "");
    setRetreatDetails(data.description || "");
    setEmailMessage(data.mail_msg || "");
    setPaymentLink(data.paymentLink || "");
    setLocation(data.location || data.parishLocation || "");

    const sched = data.scheduleParsed || [];
    const hasScheduleDates = sched.some((d) => d?.date);
    if (hasScheduleDates) {
      const findDay = (name: string) =>
        sched.find((d) => {
          const target = name.toLowerCase();
          const dAny = d as any;
          const dName = String(dAny.day || dAny.dayName || dAny.label || "").toLowerCase();
          if (dName.includes(target)) return true;
          if (d.date && moment(d.date).isValid()) {
            const dateDay = moment(d.date).format("dddd").toLowerCase();
            if (dateDay.includes(target)) return true;
          }
          return false;
        });
      const byIndex = (i: number) => sched[i];
      const fri = findDay("fri") || byIndex(0);
      const sat = findDay("sat") || byIndex(1);
      const sun = findDay("sun") || byIndex(2);
      if (fri) {
        setFriday({
          day: "Friday",
          date: String(fri.date || ""),
          from: toTimePickerValue(fri.from || fri.start, ""),
          to: toTimePickerValue(fri.to || fri.end, ""),
        });
      }
      if (sat) {
        setSaturday({
          day: "Saturday",
          date: String(sat.date || ""),
          from: toTimePickerValue(sat.from || sat.start, ""),
          to: toTimePickerValue(sat.to || sat.end, ""),
        });
      }
      if (sun) {
        setSunday({
          day: "Sunday",
          date: String(sun.date || ""),
          from: toTimePickerValue(sun.from || sun.start, ""),
          to: toTimePickerValue(sun.to || sun.end, ""),
        });
      }
    } else {
      const weekend = weekendScheduleFromDateFrom(data.dateFrom);
      if (weekend) {
        setFriday(weekend.friday);
        setSaturday(weekend.saturday);
        setSunday(weekend.sunday);
      }
    }

    const lead = data.leadership || {};
    const team = Array.isArray(lead.retreat_team) ? lead.retreat_team : [];
    setLeadership({
      leadCoupleId:
        matchCoupleId(team, "lead_couple", coupleList, (lead.leadCoupleId as number) || data.leadCoupleId || "") ||
        "",
      coLeadCoupleId:
        matchCoupleId(team, "co_lead_couple", coupleList, (lead.coLeadCoupleId as number) || "") ||
        "",
      mentorCoupleId:
        matchCoupleId(team, "mentor_couple", coupleList, (lead.mentorCoupleId as number) || "") ||
        "",
      spiritualMentorCoupleId:
        matchCoupleId(
          team,
          "spiritual_mentor_couple",
          coupleList,
          (lead.spiritualMentorCoupleId as number) || "",
        ) || "",
      spiritualDirectorPriestId:
        (lead.spiritualDirectorPriestId as number) ||
        team.find((m: any) => m.role === "spiritual_director")?.priestId ||
        "",
    });

    setRetreatType(data.retreatType || "");
    setSeason((data.season as SeasonOption) || "");
    setSeasonYear(data.seasonYear || "");
    const notes = Array.isArray(data.planningNotesList) ? data.planningNotesList : [];
    setPlanningNotes(notes.length ? String(notes[notes.length - 1]?.text || "") : "");
    const admins = Array.isArray(data.rtpAdmins) ? data.rtpAdmins : [];
    setSubAdmins(
      admins
        .map((item: any) => ({
          label: item.label || formatCoupleOptionLabel(item) || String(item.value || item.id || ""),
          value: Number(item.value || item.id || item.coupleId),
        }))
        .filter((item) => item.value),
    );
    const committee = Array.isArray(lead.committeeMembers) ? lead.committeeMembers : [];
    setCommitteeMembers(
      committee
        .map((item: any) => ({
          label:
            item.label ||
            formatCoupleOptionLabel(item) ||
            String(item.value || item.id || item.coupleId || ""),
          value: Number(item.value || item.id || item.coupleId),
        }))
        .filter((item) => item.value),
    );
    setThemeImageFile(null);
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setEditorsReady(false);
      try {
        const [detailRes, coupleRes, parishRes, priestRes] = await Promise.all([
          retreatApi.getRetreatSetupDetail({ id: Number(id) }),
          adminApi.allCouples(),
          authApi.getPerishList(),
          priestApi.getAllPriests({ pageNo: 1, size: 20, export: true }),
        ]);
        const coupleList =
          coupleRes?.success && Array.isArray(coupleRes.data) ? coupleRes.data : [];
        if (coupleList.length) setCouples(coupleList);
        if (Array.isArray(parishRes?.data)) setParishes(parishRes.data);
        if (priestRes?.success && Array.isArray(priestRes.data)) {
          setPriests(priestRes.data);
        } else if (Array.isArray(priestRes?.data)) {
          setPriests(priestRes.data);
        }
        if (detailRes?.success) {
          const data = detailRes.data;
          hydrate(data, coupleList);
          if (!htmlHasText(data.mission_statement || "")) {
            try {
              const lang = String(data.language || "english").toLowerCase();
              const sampleRes: any = await sampleContentApi.getSampleContent(
                "Mission Statement",
                lang,
              );
              if (sampleRes?.success && sampleRes.data?.content) {
                setMissionStatement(sampleRes.data.content);
              }
            } catch {
              // Sample content is optional; leave the field empty if lookup fails.
            }
          }
          setEditorsReady(true);
        } else {
          toast.error(detailRes?.msg || "Failed to load retreat.");
        }
      } catch (err: any) {
        toast.error(err?.toString?.() || "Failed to load retreat.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!editorsReady) return;
    const selectedLang = language || "english";
    sampleContentApi
      .getSampleContent("Retreat Description", selectedLang)
      .then((res: any) => {
        if (res?.success && res.data?.content) setDynamicDescription(res.data.content);
        else setDynamicDescription("");
      })
      .catch(() => setDynamicDescription(""));
    sampleContentApi
      .getSampleContent("Retreat Email Message", selectedLang)
      .then((res: any) => {
        if (res?.success && res.data?.content) setDynamicEmailMsg(res.data.content);
        else setDynamicEmailMsg("");
      })
      .catch(() => setDynamicEmailMsg(""));
    sampleContentApi
      .getSampleContent("Mission Statement", selectedLang)
      .then((res: any) => {
        if (res?.success && res.data?.content) {
          setMissionStatement((prev) => (htmlHasText(prev) ? prev : res.data.content));
        }
      })
      .catch(() => undefined);
  }, [language, editorsReady]);

  const setupReadiness = useMemo(
    () =>
      computeSetupReadinessAreas({
        themeTitle,
        themeImage,
        missionStatement,
        retreatDetails,
        emailMessage,
        paymentLink,
        schedule: {
          friday: { date: friday.date, from: friday.from, to: friday.to },
          saturday: { date: saturday.date, from: saturday.from, to: saturday.to },
          sunday: { date: sunday.date, from: sunday.from, to: sunday.to },
        },
        leadership: {
          leadCoupleId: leadership.leadCoupleId,
          coLeadCoupleId: leadership.coLeadCoupleId,
          mentorCoupleId: leadership.mentorCoupleId,
        },
        rtpAssigned: subAdmins.length > 0,
      }),
    [
      themeTitle,
      themeImage,
      missionStatement,
      retreatDetails,
      emailMessage,
      paymentLink,
      friday,
      saturday,
      sunday,
      leadership.leadCoupleId,
      leadership.coLeadCoupleId,
      leadership.mentorCoupleId,
      subAdmins,
    ],
  );
  const readyForReview = setupReadiness.readyForReview;
  const isAdmin =
    userData?.userRole === "ADMIN" ||
    userData?.isAdminRight === 1 ||
    userData?.isAdminRight === true;

  const applyWeekendDates = (sourceIndex: number, date: Date | null) => {
    if (!date) {
      setFriday((s) => ({ ...s, date: "" }));
      setSaturday((s) => ({ ...s, date: "" }));
      setSunday((s) => ({ ...s, date: "" }));
      return;
    }
    const base = date;
    const shift = (days: number) => {
      const next = new Date(base);
      next.setDate(base.getDate() + days);
      return formatScheduleDate(next);
    };
    if (sourceIndex === 0) {
      setFriday((s) => ({ ...s, date: formatScheduleDate(base) }));
      setSaturday((s) => ({ ...s, date: shift(1) }));
      setSunday((s) => ({ ...s, date: shift(2) }));
    } else if (sourceIndex === 1) {
      setFriday((s) => ({ ...s, date: shift(-1) }));
      setSaturday((s) => ({ ...s, date: formatScheduleDate(base) }));
      setSunday((s) => ({ ...s, date: shift(1) }));
    } else {
      setFriday((s) => ({ ...s, date: shift(-2) }));
      setSaturday((s) => ({ ...s, date: shift(-1) }));
      setSunday((s) => ({ ...s, date: formatScheduleDate(base) }));
    }
  };

  const updateDayTime = (
    day: "friday" | "saturday" | "sunday",
    field: "from" | "to",
    time: string | null,
  ) => {
    const current =
      day === "friday" ? friday : day === "saturday" ? saturday : sunday;
    const setter =
      day === "friday" ? setFriday : day === "saturday" ? setSaturday : setSunday;
    const nextVal = time || "";
    const other = field === "from" ? current.to : current.from;
    if (nextVal && other && nextVal === other) {
      setSameTimeError(day);
      return;
    }
    setSameTimeError("");
    setter({ ...current, [field]: nextVal });
  };

  const buildPayload = () => {
    const schedule = [friday, saturday, sunday];
    const existingLead = retreat?.leadership || {};
    const existingTeam = Array.isArray(existingLead.retreat_team)
      ? existingLead.retreat_team
      : [];
    const fillCouple = (role: string, coupleId: number | "") => {
      const c = couples.find((x) => x.id === coupleId);
      const prev = existingTeam.find((m: any) => m.role === role) || { role };
      if (!c) return { ...prev, role };
      return {
        role,
        hisFirstName: c.hisFirstName || "",
        hisLastName: c.hisLastName || "",
        hisEmail: c.hisEmail || "",
        herFirstName: c.herFirstName || "",
        herLastName: c.herLastName || "",
        herEmail: c.herEmail || "",
      };
    };
    const fillPriest = () => {
      const p = priests.find((x) => x.id === leadership.spiritualDirectorPriestId);
      const prev =
        existingTeam.find((m: any) => m.role === "spiritual_director") || {
          role: "spiritual_director",
        };
      if (!p) return { ...prev, role: "spiritual_director" };
      return {
        role: "spiritual_director",
        priestId: p.id,
        firstName: p.firstName || "",
        lastName: p.lastName || p.name || "",
        email: p.email || "",
        hisFirstName: p.firstName || "",
        hisLastName: p.lastName || p.name || "",
        hisEmail: p.email || "",
      };
    };
    const retreat_team = [
      fillCouple("lead_couple", leadership.leadCoupleId),
      fillCouple("co_lead_couple", leadership.coLeadCoupleId),
      fillCouple("mentor_couple", leadership.mentorCoupleId),
      fillCouple("spiritual_mentor_couple", leadership.spiritualMentorCoupleId),
      fillPriest(),
      ...existingTeam.filter(
        (m: any) =>
          ![
            "lead_couple",
            "co_lead_couple",
            "mentor_couple",
            "spiritual_mentor_couple",
            "spiritual_director",
          ].includes(m.role),
      ),
    ];
    const leadershipJson = {
      ...existingLead,
      leadCoupleId: leadership.leadCoupleId || null,
      coLeadCoupleId: leadership.coLeadCoupleId || null,
      mentorCoupleId: leadership.mentorCoupleId || null,
      spiritualMentorCoupleId: leadership.spiritualMentorCoupleId || null,
      spiritualDirectorPriestId: leadership.spiritualDirectorPriestId || null,
      retreat_team,
      committeeMembers,
    };
    const selectedParish = parishes.find((p) => p.id === parishId);
    const publicTitleValue =
      formatLanguage(language) === "Spanish"
        ? `${selectedParish?.parish || retreat?.parishName || retreat?.title || ""} (Spanish)`
        : selectedParish?.parish || retreat?.parishName || retreat?.title || "";

    return {
      id: Number(id),
      parishId: parishId || null,
      language,
      title: publicTitleValue,
      heading: themeTitle,
      image1: themeImage?.startsWith("blob:") ? undefined : themeImage,
      mission_statement: missionStatement,
      description: retreatDetails,
      mail_msg: emailMessage,
      paymentLink,
      location,
      schedule,
      leadershipJson,
      season: season || null,
      seasonYear: seasonYear || null,
      retreatType: retreatType || null,
      planningNotes,
      committeeMembers,
      ...actorPayload(),
    };
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let res: any;
      if (themeImageFile) {
        const fd = new FormData();
        fd.append("file1", themeImageFile);
        fd.append("id", String(payload.id));
        fd.append("fromData", JSON.stringify({ ...payload, image1: undefined }));
        fd.append("actorName", String(payload.actorName || ""));
        fd.append("actorRole", String(payload.actorRole || ""));
        if (payload.actorUserId) fd.append("actorUserId", String(payload.actorUserId));
        res = await retreatApi.saveRetreatSetupProgress(fd);
      } else {
        res = await retreatApi.saveRetreatSetupProgress(payload);
      }
      if (res?.success) {
        toast.success("Progress saved.");
        setThemeImageFile(null);
        hydrate(res.data, couples);
      } else {
        toast.error(res?.msg || "Save failed.");
      }
    } catch (err: any) {
      toast.error(err?.toString?.() || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const approvePublish = async () => {
    try {
      const res = await retreatApi.approveAndPublishRetreat({
        id: Number(id),
        ...actorPayload(),
      });
      if (res?.success) {
        toast.success(res.msg || "Published.");
        navigate("/manageRetreat");
      } else {
        toast.error(res?.msg || "Publish failed.");
      }
    } catch (err: any) {
      toast.error(err?.toString?.() || "Publish failed.");
    }
  };

  const approveToSetup = async () => {
    if (!id) return;
    setApproving(true);
    try {
      await saveProgress();
      const res = await retreatApi.startRetreatSetup({
        id: Number(id),
        confirm: true,
        ...actorPayload(),
      });
      if (res?.success) {
        toast.success(res.msg || "Moved to Retreats in Setup.");
        hydrate(res.data, couples);
      } else {
        toast.error(res?.msg || "Approve failed.");
      }
    } catch (err: any) {
      toast.error(err?.toString?.() || "Approve failed.");
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!retreat) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Retreat not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/manageRetreat")}>
          Back
        </Button>
      </Box>
    );
  }

  const isProjected = retreat.lifecycleStatus === "Projected";
  const isSetupStage =
    retreat.lifecycleStatus === "Pre-Publication Setup" ||
    retreat.lifecycleStatus === "Setup in Progress" ||
    retreat.lifecycleStatus === "Ready for Publication";

  const selectedParish = parishes.find((p) => p.id === parishId);
  const publicTitle =
    formatLanguage(language) === "Spanish"
      ? `${selectedParish?.parish || retreat.parishName || retreat.title} (Spanish)`
      : selectedParish?.parish || retreat.parishName || retreat.title;

  const leadershipCards = LEADERSHIP_ROLES.map((role) => {
    if (role.kind === "priest") {
      const priest = priests.find(
        (p) =>
          Number(p.id) === Number(leadership.spiritualDirectorPriestId || 0),
      );
      const name = priestCardName(priest);
      return {
        ...role,
        name,
        initials: initialsFromName(name || role.label),
        assigned: Boolean(name),
      };
    }
    const idKey =
      role.key === "lead_couple"
        ? leadership.leadCoupleId
        : role.key === "co_lead_couple"
          ? leadership.coLeadCoupleId
          : role.key === "mentor_couple"
            ? leadership.mentorCoupleId
            : leadership.spiritualMentorCoupleId;
    const couple = couples.find((c) => Number(c.id) === Number(idKey || 0));
    const name = coupleCardName(couple);
    return {
      ...role,
      name,
      initials: initialsFromName(name || role.label),
      assigned: Boolean(name),
    };
  });

  const applyLeadershipAssignment = () => {
    if (!assignRole || !assignValue?.value) {
      toast.error("Select a person to assign.");
      return;
    }
    const value = assignValue.value;
    setLeadership((s) => {
      const next = { ...s };
      const clearIfSame = (key: keyof LeadershipState) => {
        if (Number(next[key]) === Number(value)) next[key] = "";
      };
      if (assignRole === "lead_couple") {
        clearIfSame("coLeadCoupleId");
        clearIfSame("mentorCoupleId");
        clearIfSame("spiritualMentorCoupleId");
        next.leadCoupleId = value;
      } else if (assignRole === "co_lead_couple") {
        clearIfSame("leadCoupleId");
        clearIfSame("mentorCoupleId");
        clearIfSame("spiritualMentorCoupleId");
        next.coLeadCoupleId = value;
      } else if (assignRole === "mentor_couple") {
        clearIfSame("leadCoupleId");
        clearIfSame("coLeadCoupleId");
        clearIfSame("spiritualMentorCoupleId");
        next.mentorCoupleId = value;
      } else if (assignRole === "spiritual_mentor_couple") {
        clearIfSame("leadCoupleId");
        clearIfSame("coLeadCoupleId");
        clearIfSame("mentorCoupleId");
        next.spiritualMentorCoupleId = value;
      } else {
        next.spiritualDirectorPriestId = value;
      }
      return next;
    });
    setAssignRole(null);
    setAssignValue(null);
  };

  const leadCoupleName =
    leadershipCards.find((r) => r.key === "lead_couple")?.name || "Not assigned";
  const coLeadCoupleName =
    leadershipCards.find((r) => r.key === "co_lead_couple")?.name || "";
  const mentorCoupleName =
    leadershipCards.find((r) => r.key === "mentor_couple")?.name || "";
  const retreatDatesLabel =
    friday.date && sunday.date
      ? `${moment(friday.date).format("MMM D")}–${moment(sunday.date).format("D, YYYY")}`
      : retreat.seasonLabel || "Dates pending";

  return (
    <ThemeProvider theme={SETUP_THEME}>
    <Box
      sx={{
        width: "100%",
        maxWidth: 1400,
        mx: "auto",
        pb: 6,
        px: { xs: 1, sm: 0 },
        fontFamily: `${DASHBOARD_FONT} !important`,
        minHeight: "100%",
        "&, & *": {
          fontFamily: `${DASHBOARD_FONT} !important`,
        },
        "& .MuiTypography-root, & .MuiButton-root, & .MuiButton-root *, & .MuiChip-root, & .MuiChip-label, & .MuiTab-root, & .MuiTab-root *, & .MuiAlert-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiFormHelperText-root, & .MuiSelect-select, & .MuiMenuItem-root":
          {
            fontFamily: `${DASHBOARD_FONT} !important`,
          },
        "& .MuiButton-containedPrimary": {
          backgroundColor: `${DASHBOARD_NAVY} !important`,
          color: "#ffffff !important",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: `${DASHBOARD_NAVY_HOVER} !important`,
            boxShadow: "none",
          },
        },
        "& .MuiButton-textPrimary, & .MuiButton-text:not(.MuiButton-colorWarning):not(.MuiButton-colorSuccess):not(.MuiButton-colorError)":
          {
            color: DASHBOARD_NAVY,
          },
        "& .MuiCheckbox-root.Mui-checked, & .MuiPaginationItem-root.Mui-selected":
          {
            color: DASHBOARD_NAVY,
            backgroundColor: "rgba(31, 95, 143, 0.12)",
          },
      }}
    >
      <Button
        onClick={() => navigate("/manageRetreat")}
        sx={{
          mb: 1.5,
          px: 0,
          textTransform: "none",
          color: DASHBOARD_NAVY,
          fontWeight: 600,
          fontSize: "0.875rem",
          fontFamily: DASHBOARD_FONT,
        }}
      >
        ← Back to Retreats Dashboard
      </Button>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ justifyContent: "space-between" }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                fontFamily: DASHBOARD_FONT,
                color: DASHBOARD_NAVY,
                fontWeight: 700,
                letterSpacing: "0.08em",
                lineHeight: 1.4,
              }}
            >
              Retreats in Setup
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: DASHBOARD_FONT,
                fontWeight: 700,
                color: DASHBOARD_TEXT,
                lineHeight: 1.25,
              }}
            >
              {retreat.parishName || retreat.title}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ fontFamily: DASHBOARD_FONT, fontSize: "0.875rem" }}
            >
              {formatLanguage(retreat.language)} · {retreatDatesLabel} · Lead Couple:{" "}
              {leadCoupleName}
            </Typography>
          </Box>
          <Chip
            label="Lifecycle: Retreats in Setup"
            variant="outlined"
            sx={{
              alignSelf: { xs: "flex-start", sm: "center" },
              color: DASHBOARD_NAVY,
              borderColor: "#b7c9d6",
              bgcolor: "#FFFFFF",
              fontWeight: 600,
              fontSize: "0.8125rem",
              fontFamily: DASHBOARD_FONT,
            }}
          />
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ mt: 1.5, borderRadius: 2.5, overflow: "hidden" }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Retreat Setup workstreams"
          variant={isSmallScreen ? "fullWidth" : "scrollable"}
          sx={{
            fontFamily: DASHBOARD_FONT,
            "& .MuiTab-root": {
              fontFamily: DASHBOARD_FONT,
              textTransform: "none",
              color: DASHBOARD_MUTED,
            },
            "& .Mui-selected": {
              color: `${DASHBOARD_NAVY} !important`,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: DASHBOARD_NAVY,
            },
          }}
        >
          <Tab
            id="setup-tab-publication"
            aria-controls="setup-panel-publication"
            aria-label={`Publication Readiness · ${setupReadiness.completedAreaCount} of ${setupReadiness.areas.length} readiness areas complete`}
            label={
              <Box
                component="span"
                sx={{ display: "grid", textAlign: "center", lineHeight: 1.15 }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 800, color: "inherit" }}
                >
                  Publication Readiness
                </Typography>
                <Typography component="span" variant="caption" sx={{ color: "inherit" }}>
                  {setupReadiness.completedAreaCount} of {setupReadiness.areas.length} areas
                  complete
                </Typography>
              </Box>
            }
          />
          <Tab
            id="setup-tab-team"
            aria-controls="setup-panel-team"
            aria-label={
              teamFormationReviewStatus
                ? `Team Formation · ${teamFormationSummary.inConsideration} in consideration · ${teamFormationSummary.firstTimePercentage}% first-time · ${teamFormationReviewStatus}`
                : `Team Formation · ${teamFormationSummary.inConsideration} in consideration · ${teamFormationSummary.firstTimePercentage}% first-time`
            }
            label={
              <Box
                component="span"
                sx={{ display: "grid", textAlign: "center", lineHeight: 1.15 }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 800, color: "inherit" }}
                >
                  Team Formation
                </Typography>
                <Typography component="span" variant="caption" sx={{ color: "inherit" }}>
                  {teamFormationSummary.inConsideration} in consideration ·{" "}
                  {teamFormationSummary.firstTimePercentage}% first-time
                  {teamFormationReviewStatus ? ` · ${teamFormationReviewStatus}` : ""}
                </Typography>
              </Box>
            }
          />
        </Tabs>
      </Paper>

          <Box
            role="tabpanel"
            id="setup-panel-publication"
            aria-labelledby="setup-tab-publication"
            hidden={activeTab !== 0}
          >
            <Paper
              elevation={2}
              sx={{
                mt: 3,
                borderRadius: 3,
                overflow: "hidden",
                fontFamily: DASHBOARD_FONT,
                "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiInputBase-root, & .MuiFormLabel-root, & .MuiFormHelperText-root, & .MuiAlert-root":
                  {
                    fontFamily: DASHBOARD_FONT,
                  },
                "& .MuiInputBase-root": { fontSize: "0.875rem" },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiFormHelperText-root": { fontSize: "0.75rem" },
                "& .MuiButton-root": {
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  textTransform: "none",
                },
              }}
            >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            bgcolor: "#192f4d",
            color: "white",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 1, sm: 2 },
            alignItems: { xs: "stretch", lg: "center" },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontFamily: DASHBOARD_FONT,
                  fontWeight: 800,
                  fontSize: { xs: "1.15rem", sm: "1.35rem" },
                  lineHeight: 1.25,
                }}
              >
                Publication Readiness
              </Typography>
              <Chip
                label="Retreats in Setup"
                size="small"
                sx={{
                  bgcolor: "#f4c95d",
                  color: "#1A1A1A",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  height: 24,
                }}
              />
            </Stack>
            <Typography
              sx={{
                opacity: 0.85,
                mt: 0.5,
                fontSize: "0.8125rem",
                lineHeight: 1.45,
                display: { xs: "none", sm: "block" },
              }}
            >
              Complete the public page, required leadership, schedule, and registration
              administration needed for publication.
            </Typography>
          </Box>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              flexWrap: { xs: "nowrap", sm: "wrap" },
              overflowX: { xs: "auto", sm: "visible" },
              pb: { xs: 0.5, sm: 0 },
              "& > *": { flexShrink: 0 },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<Save size={14} />}
              onClick={saveProgress}
              disabled={saving}
              sx={{ color: "white", borderColor: "rgba(255,255,255,.65)" }}
            >
              Save Progress
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Eye size={14} />}
              onClick={() =>
                window.open(
                  `/registration-retreat/${retreat.id}?preview=true`,
                  "_blank",
                )
              }
              sx={{ color: "white", borderColor: "rgba(255,255,255,.65)" }}
            >
              Preview
            </Button>
            {isProjected && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircle2 size={14} />}
                disabled={saving || approving}
                onClick={approveToSetup}
              >
                {approving ? "Approving…" : "Approve"}
              </Button>
            )}
            {isAdmin && isSetupStage && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircle2 size={14} />}
                disabled={!readyForReview}
                onClick={approvePublish}
              >
                Approve & Publish
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <PublicationReadinessDashboard
            readiness={setupReadiness}
            onNavigate={goToField}
          />

          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <SectionCard
              title="Retreat Identity"
              subtitle="Parish data generates the public location and retreat title automatically."
            >
              <Stack spacing={1.5}>
                <FormControl fullWidth>
                  <InputLabel id="parish-label">Selected Parish</InputLabel>
                  <Select
                    labelId="parish-label"
                    label="Selected Parish"
                    value={parishId}
                    onChange={(e) => {
                      const nextId = e.target.value as number;
                      setParishId(nextId);
                      const parish = parishes.find((p) => p.id === nextId);
                      if (parish) {
                        const nextLocation = [parish.city, parish.state]
                          .filter(Boolean)
                          .join(", ");
                        if (nextLocation) setLocation(nextLocation);
                      }
                    }}
                  >
                    {parishes.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {formatParishOptionLabel(p)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    label="Language"
                    value={language}
                    onChange={(e) => setLanguage(String(e.target.value))}
                  >
                    <MenuItem value="english">English</MenuItem>
                    <MenuItem value="spanish">Spanish</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Location / Address"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
                <TextField
                  label="Public Retreat Title"
                  value={publicTitle}
                  InputProps={{ readOnly: true }}
                  helperText="Generated from parish name and language."
                />
                <Box
                  id={setupFieldId("theme-title")}
                  sx={fieldAnchorSx(highlightedFieldId === setupFieldId("theme-title"))}
                >
                  <TextField
                    required
                    label="Retreat Theme (Title)"
                    value={themeTitle}
                    onChange={(e) => setThemeTitle(e.target.value)}
                    helperText="The retreat-specific theme displayed on the public page."
                    fullWidth
                  />
                </Box>
                <Stack
                  id={setupFieldId("theme-image")}
                  spacing={1.5}
                  sx={{
                    alignItems: "flex-start",
                    ...fieldAnchorSx(highlightedFieldId === setupFieldId("theme-image")),
                  }}
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept={RETREAT_IMAGE_ACCEPT}
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      try {
                        const processed = await processRetreatImage(file);
                        setThemeImageFile(processed);
                        setThemeImage(URL.createObjectURL(processed));
                        toast.success("Theme image ready to save.");
                      } catch (err: any) {
                        toast.error(err?.message || "Could not process image.");
                      }
                    }}
                  />
                  <div className="edit-profile-img text-center">
                    <img
                      src={
                        getRetreatImagePreviewSrc(themeImageFile || themeImage) ||
                        uploadRetreat
                      }
                      alt="Theme"
                      style={{ width: 280, height: "auto", maxWidth: "100%" }}
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.onerror = null;
                        img.src = corruptedImg;
                      }}
                    />
                  </div>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={themeImage ? "outlined" : "contained"}
                      startIcon={<Upload size={14} />}
                      onClick={() => imageInputRef.current?.click()}
                    >
                      {themeImage ? "Change Theme Image" : "Upload Theme Image"}
                    </Button>
                    {themeImage ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setThemeImage("");
                          setThemeImageFile(null);
                        }}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Retreat Type</InputLabel>
                    <Select
                      label="Retreat Type"
                      value={retreatType}
                      onChange={(e) => setRetreatType(e.target.value as string)}
                    >
                      <MenuItem value="">—</MenuItem>
                      {RETREAT_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Season</InputLabel>
                    <Select
                      label="Season"
                      value={season}
                      onChange={(e) => setSeason(e.target.value as SeasonOption | "")}
                    >
                      <MenuItem value="">—</MenuItem>
                      {SEASON_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Year"
                    type="number"
                    size="small"
                    value={seasonYear}
                    onChange={(e) =>
                      setSeasonYear(e.target.value ? Number(e.target.value) : "")
                    }
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Planning notes"
                  value={planningNotes}
                  onChange={(e) => setPlanningNotes(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  sx={{ "& textarea": { height: "auto !important", overflow: "auto" } }}
                />
              </Stack>
            </SectionCard>

            <SectionCard
              title="Friday-Sunday Schedule"
              subtitle="The schedule reflows into stacked day cards on narrow screens."
            >
              <Stack spacing={1.25}>
                <Box
                  id={setupFieldId("friday")}
                  sx={fieldAnchorSx(highlightedFieldId === setupFieldId("friday"))}
                >
                  <WeekendDayFields
                    day="Friday"
                    weekday={5}
                    value={friday}
                    onDateChange={(date) => applyWeekendDates(0, date)}
                    onFromChange={(time) => updateDayTime("friday", "from", time)}
                    onToChange={(time) => updateDayTime("friday", "to", time)}
                    sameTimeError={sameTimeError === "friday"}
                  />
                </Box>
                <Box
                  id={setupFieldId("saturday")}
                  sx={fieldAnchorSx(highlightedFieldId === setupFieldId("saturday"))}
                >
                  <WeekendDayFields
                    day="Saturday"
                    weekday={6}
                    value={saturday}
                    onDateChange={(date) => applyWeekendDates(1, date)}
                    onFromChange={(time) => updateDayTime("saturday", "from", time)}
                    onToChange={(time) => updateDayTime("saturday", "to", time)}
                    sameTimeError={sameTimeError === "saturday"}
                  />
                </Box>
                <Box
                  id={setupFieldId("sunday")}
                  sx={fieldAnchorSx(highlightedFieldId === setupFieldId("sunday"))}
                >
                  <WeekendDayFields
                    day="Sunday"
                    weekday={0}
                    value={sunday}
                    onDateChange={(date) => applyWeekendDates(2, date)}
                    onFromChange={(time) => updateDayTime("sunday", "from", time)}
                    onToChange={(time) => updateDayTime("sunday", "to", time)}
                    sameTimeError={sameTimeError === "sunday"}
                  />
                </Box>
              </Stack>
            </SectionCard>
          </Box>

          <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
            <SectionCard
              id={setupFieldId("mission")}
              title="Mission Statement"
              highlighted={highlightedFieldId === setupFieldId("mission")}
            >
              {editorsReady && (
                <SetupRichEditor
                  editorKey={`mission-${retreat.id}`}
                  value={missionStatement}
                  onChange={setMissionStatement}
                  height="180px"
                />
              )}
            </SectionCard>
            <SectionCard
              id={setupFieldId("details")}
              title="Retreat Details"
              highlighted={highlightedFieldId === setupFieldId("details")}
              subtitle="Public-facing information: cost and deposit, what registration includes, meals, capacity or availability, attendance expectations, childcare, venue/contact information, and other participant instructions."
              actions={
                <SampleLink onClick={() => setDescriptionSample(true)}>
                  Description Sample
                </SampleLink>
              }
            >
              {editorsReady && (
                <SetupRichEditor
                  editorKey={`details-${retreat.id}`}
                  value={retreatDetails}
                  onChange={setRetreatDetails}
                  height="280px"
                />
              )}
            </SectionCard>
            <SectionCard
              id={setupFieldId("email")}
              title="Registration Confirmation Email"
              highlighted={highlightedFieldId === setupFieldId("email")}
              subtitle="The message sent after registration. Use Preview to review it with the complete public page before publication."
              actions={
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <SampleLink onClick={() => setMailMsgView(true)}>
                    Preview Email
                  </SampleLink>
                  <SampleLink onClick={() => setMailSample(true)}>
                    Sample Email Message
                  </SampleLink>
                </Stack>
              }
            >
              {editorsReady && (
                <SetupRichEditor
                  editorKey={`email-${retreat.id}`}
                  value={emailMessage}
                  onChange={setEmailMessage}
                  height="280px"
                />
              )}
            </SectionCard>
            <SectionCard
              id={setupFieldId("payment")}
              title="Registration Payment"
              highlighted={highlightedFieldId === setupFieldId("payment")}
            >
              <TextField
                required
                label="Retreat payment link"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                fullWidth
                helperText="Required even for sponsored/outreach retreats that use a deposit."
              />
            </SectionCard>
          </Box>

          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <SectionCard
              title="Retreat Leadership"
              subtitle="Assign the publication leadership displayed on the public registration page. Team Formation manages internal team leadership separately."
            >
              <Stack spacing={1}>
                {leadershipCards.map((assignment) => (
                  <Paper
                    key={assignment.key}
                    id={leadershipSetupFieldId(assignment.key)}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderLeftWidth: 5,
                      borderLeftColor: assignment.assigned ? "#3a9a4e" : "#d79b1b",
                      bgcolor: assignment.assigned ? "#edf8ef" : "#fff8e6",
                      ...fieldAnchorSx(
                        highlightedFieldId === leadershipSetupFieldId(assignment.key),
                      ),
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      sx={{ gap: 1, alignItems: { xs: "stretch", sm: "center" } }}
                    >
                      <Avatar sx={{ bgcolor: "#a77c18", width: 36, height: 36, fontSize: "0.8125rem" }}>
                        {assignment.initials}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: "0.875rem" }}>
                          {assignment.label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem" }}>
                          {assignment.name || "No assignment selected"}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant={assignment.assigned ? "outlined" : "contained"}
                        onClick={() => {
                          setAssignRole(assignment.key);
                          setAssignValue(null);
                        }}
                      >
                        {assignment.assigned ? "Change" : "Assign"}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </SectionCard>

            <SectionCard
              id={setupFieldId("rtp")}
              title="Registration Administration — RTP"
              highlighted={highlightedFieldId === setupFieldId("rtp")}
              subtitle="Required before publication. RTP manages registration, treasury, participant records, and photography-related registration functions."
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    RTP Registration Administrator
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <SelectReact
                        options={rtpCoupleOptions}
                        value={adminSelect}
                        onChange={(val: any) => setAdminSelect(val || null)}
                        isSearchable
                        isClearable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                          control: (base) => ({ ...base, minHeight: 38 }),
                        }}
                        placeholder="Select administrator"
                      />
                    </Box>
                    {adminTick ? (
                      <CheckCircle2 size={28} color="#2e7d32" />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Plus size={16} />}
                        onClick={async () => {
                          if (!adminSelect?.value) {
                            toast.error("Select at least one couple.");
                            return;
                          }
                          const alreadyAssigned = subAdmins.some(
                            (ex) => Number(ex.value) === Number(adminSelect.value),
                          );
                          if (alreadyAssigned) {
                            toast.info("This couple is already assigned as RTP administrator.");
                            setAdminSelect(null);
                            return;
                          }
                          try {
                            const res: any = await retreatApi.giveAccesssOfApprove({
                              coupleIds: [
                                {
                                  label: adminSelect.label,
                                  value: adminSelect.value,
                                },
                              ],
                              retreatId: Number(id),
                            });
                            if (!res?.success) {
                              toast.error(res?.msg || "Could not assign RTP administrator.");
                              return;
                            }
                            setSubAdmins((prev) => [
                              ...prev,
                              {
                                label: adminSelect.label,
                                value: adminSelect.value,
                              },
                            ]);
                            toast.success("RTP registration administrator assigned.");
                            setAdminSelect(null);
                            setAdminTick(true);
                            setTimeout(() => setAdminTick(false), 1500);
                          } catch (err: any) {
                            toast.error(err?.toString?.() || "Could not assign RTP administrator.");
                          }
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </Stack>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {subAdmins.map((item) => (
                      <Stack
                        key={item.value}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1 }}
                      >
                        <Typography variant="body2">{item.label}</Typography>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Trash2 size={14} />}
                          onClick={async () => {
                            try {
                              const res: any = await retreatApi.removeAccessFromRetreat({
                                retreatId: Number(id),
                                coupleId: item.value,
                              });
                              if (!res?.success) {
                                toast.error(res?.msg || "Could not remove RTP administrator.");
                                return;
                              }
                              setSubAdmins((prev) =>
                                prev.filter((a) => a.value !== item.value),
                              );
                            } catch (err: any) {
                              toast.error(err?.toString?.() || "Could not remove RTP administrator.");
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                                    <Alert
                    severity={subAdmins.length ? "success" : "warning"}
                    sx={{ mt: 1.5 }}
                  >
                    {subAdmins.length
                      ? "RTP registration administration is assigned."
                      : "RTP registration administration is required before submission."}
                  </Alert>
                </Box>

                {retreat.retreatType === "Archbishop Outreach Retreat" && (
                  <Alert severity="info">
                    Outreach funding/venue planning fields can expand in later
                    phases. Payment configuration remains required for deposits.
                  </Alert>
                )}
              </Stack>
            </SectionCard>
          </Box>

          <Box sx={{ mt: 2 }}>
            <SectionCard
              title="Steering Committee"
              subtitle="Add or remove steering committee couples for this retreat. Changes are saved with Progress."
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SelectReact
                      options={committeeCoupleOptions}
                      value={committeeSelect}
                      onChange={(val: any) => setCommitteeSelect(val || [])}
                      isSearchable
                      isMulti
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                        control: (base) => ({ ...base, minHeight: 38 }),
                      }}
                      placeholder="Select committee members"
                    />
                  </Box>
                  {committeeTick ? (
                    <CheckCircle2 size={28} color="#2e7d32" />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Plus size={16} />}
                      onClick={() => {
                        const selected = Array.isArray(committeeSelect)
                          ? committeeSelect
                          : committeeSelect
                            ? [committeeSelect]
                            : [];
                        if (!selected.length) {
                          toast.error("Select at least one couple.");
                          return;
                        }
                        setCommitteeMembers((prev) => {
                          const merged = [
                            ...prev,
                            ...selected
                              .map((opt: any) => ({
                                label: opt.label,
                                value: Number(opt.value),
                              }))
                              .filter(
                                (item: { value: number }) =>
                                  item.value &&
                                  !prev.some((ex) => Number(ex.value) === Number(item.value)),
                              ),
                          ];
                          return merged;
                        });
                        toast.success("Steering committee updated.");
                        setCommitteeSelect([]);
                        setCommitteeTick(true);
                        setTimeout(() => setCommitteeTick(false), 1500);
                      }}
                    >
                      Add
                    </Button>
                  )}
                </Stack>
                {committeeMembers.length ? (
                  <Stack spacing={1}>
                    {committeeMembers.map((item) => (
                      <Stack
                        key={item.value}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1 }}
                      >
                        <Typography variant="body2">{item.label}</Typography>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Trash2 size={14} />}
                          onClick={() =>
                            setCommitteeMembers((prev) =>
                              prev.filter((a) => a.value !== item.value),
                            )
                          }
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No committee members yet.
                  </Typography>
                )}
              </Stack>
            </SectionCard>
          </Box>
        </Box>
      </Paper>
          </Box>
          <Box
            role="tabpanel"
            id="setup-panel-team"
            aria-labelledby="setup-tab-team"
            hidden={activeTab !== 1}
            sx={{ mt: 3 }}
          >
            <TeamFormationTab
              parishName={retreat.parishName || retreat.title || ""}
              seasonLabel={retreat.seasonLabel || ""}
              leadName={
                leadCoupleName !== "Not assigned" ? leadCoupleName : undefined
              }
              coLeadName={coLeadCoupleName || undefined}
              mentorName={mentorCoupleName || undefined}
              onSummaryChange={handleTeamSummaryChange}
              onReviewStatusChange={handleTeamReviewStatusChange}
            />
          </Box>

      {mailMsgView ? (
        <ViewMailMsg
          show={mailMsgView}
          alertHeader="Preview Email"
          alertMessage={emailMessage}
          handleClose={() => setMailMsgView(false)}
          language={language}
        />
      ) : null}

      {mailSample ? (
        <AlertDelete
          show={mailSample}
          alertHeader="Sample Email Message"
          alerMessage={
            <div>
              {dynamicEmailMsg ? (
                <div dangerouslySetInnerHTML={{ __html: dynamicEmailMsg }} />
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: fallbackEmailHtml(language) }}
                />
              )}
            </div>
          }
          handleClose={() => setMailSample(false)}
          buttonText="Close"
        />
      ) : null}

      {descriptionSample ? (
        <AlertDelete
          show={descriptionSample}
          alertHeader="Description Sample"
          alerMessage={
            <div>
              {dynamicDescription ? (
                <div dangerouslySetInnerHTML={{ __html: dynamicDescription }} />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fallbackDescriptionHtml(language),
                  }}
                />
              )}
            </div>
          }
          handleClose={() => setDescriptionSample(false)}
          buttonText="Close"
        />
      ) : null}

      <Dialog
        open={Boolean(assignRole)}
        onClose={() => {
          setAssignRole(null);
          setAssignValue(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { overflow: "visible" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {assignRole
            ? `Assign ${LEADERSHIP_ROLES.find((r) => r.key === assignRole)?.label}`
            : "Assign"}
        </DialogTitle>
        <DialogContent dividers sx={{ overflow: "visible" }}>
          <SelectReact
            options={
              assignRole === "spiritual_director"
                ? priestOptions
                : leadershipCoupleOptions
            }
            value={assignValue}
            onChange={(opt) => setAssignValue(opt)}
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              menu: (base) => ({ ...base, zIndex: 2000 }),
            }}
            placeholder={
              assignRole === "spiritual_director"
                ? "Select priest / deacon"
                : "Select couple"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssignRole(null);
              setAssignValue(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={applyLeadershipAssignment}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

export default RetreatSetupWorkspace;
